import { useEffect, useState } from "react"
import { useRouter } from "next/router"
import { RadioGroup } from "@headlessui/react"
import { CheckIcon } from "@heroicons/react/solid"
import { DateTime } from "luxon"

import { useAuth } from "hooks/auth"

import Input from "components/Input"
import Button from "components/Button"
import AuthLayout from "components/layouts/Auth"
import Checkbox from "components/Checkbox"
import Link from "next/link"
import Alert from "components/Alert"
import DateInput from "components/DateInput"

const scenarios = [
  {
    id: 1,
    title: 'I am using this for myself ',
  },
  {
    id: 2,
    title: 'I am using this on behalf of someone else',
  },
  {
    id: 3,
    title: 'I am helping administer a deceased estate',
  }
]

export default function Register() {

  // Access regiser 
  const { register } = useAuth({
    middleware: 'guest',
    redirectIfAuthenticated: '/dashboard',
  })

  // Local state 
  const [errors, setErrors] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState('SCENARIO')
  const [scenario, setScenario] = useState(null)
  const [estate, setEstate] = useState({
    name: '',
    date_of_death: ''
  })
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    terms: false,
    marketing: false
  })

  /**
   * Watch for user update
   */
  useEffect(() => {

    // Prefill estate name 
    if (scenario == 1) setEstate({ ...estate, name: `${newUser.first_name} ${newUser.last_name}` })

  }, [newUser])

  /**
   * Validate estate 
   */
  const validateEstate = (e) => {
    e.preventDefault()

    if (!scenario) {
      setErrors('Please select an estate type')
      return false
    }

    setErrors([])
    if (scenario > 1) {
      if (!estate.name) {
        setErrors('Please enter the estate name')
        return false
      }
      if (scenario == 3) {
        if (!estate.date_of_death) {
          setErrors('Please enter the date of death')
          return false
        }
      }
    }

    // Move onto next step 
    setStep('DETAILS')
    return false

  }

  /**
   * Handle submission 
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Register 
    setSubmitting(true)
    const user = await register({ ...newUser, setErrors, estate })
    if (!user) setSubmitting(false)
  }

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit}>
        {
          step == 'SCENARIO' ? (
            <div>
              <div>
                <h2 className="text-4xl font-bold text-gray-800 tracking-tight">Set up your estate</h2>
                {/* <p className="text-gray-600 text-lg mt-2">Choose how you want your estate to look like</p> */}
              </div>
              <div className="mt-8 space-y-4">
                <RadioGroup value={scenario} onChange={setScenario}>
                  <RadioGroup.Label className="sr-only">Server size</RadioGroup.Label>
                  <div className="grid gap-4 mt-8">
                    {scenarios.map((option) => (
                      <RadioGroup.Option
                        key={option.title}
                        value={option.id}
                        className={({ active, checked }) => `
                          ${active ? 'ring-2 ring-offset-2 ring-offset-yellow-100 ring-yellow-100' : ''}
                          ${checked ? 'border-yellow-300 bg-yellow-50 text-yellow-700' : 'bg-white border-gray-200'}
                          relative rounded-lg px-5 py-4 cursor-pointer flex border focus:outline-none
                      `}
                      >
                        {({ active, checked }) => (
                          <>
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <div className="text-sm">
                                  <RadioGroup.Label
                                    as="p"
                                    className={`font-medium text-base ${checked ? 'text-yellow-700' : 'text-gray-900'
                                      }`}
                                  >
                                    {option.title}
                                  </RadioGroup.Label>
                                </div>
                              </div>
                              {checked && (
                                <div className="flex-shrink-0 text-white bg-yellow-400 rounded-full p-1">
                                  <CheckIcon className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
                {
                  scenario == 2 && (
                    <Input label="Their full name" size="lg" type="text" value={estate.name} onChange={(e) => setEstate({ ...estate, name: e.target.value })} placeholder="Jane Doe" />
                  )
                }
                {
                  scenario == 3 && (
                    <>
                      <Input label="Deceased full name" size="lg" type="text" value={estate.name} onChange={(e) => setEstate({ ...estate, name: e.target.value })} placeholder="Jane Doe" />
                      <DateInput
                        size="lg"
                        label="Date of death"
                        value={estate.date_of_death ? new Date(DateTime.fromISO(estate.date_of_death).toHTTP()) : new Date()}
                        onChange={(val) => setEstate({ ...estate, date_of_death: DateTime.fromJSDate(val).toISO() })}
                      />
                    </>
                  )
                }
              </div>
              {
                errors?.length > 0 && (
                  <div className="mt-4">
                    <Alert type="error" list={errors} />
                  </div>
                )
              }
              <div className="flex flex-col space-y-4 mt-10">
                <Button color="black" size="lg" onClick={validateEstate}>
                  Continue
                </Button>
                <Button color="clear" href="/login" size="lg">Login with my existing account</Button>
              </div>
            </div>
          ) : (
            <div>
              <div>
                <h2 className="text-4xl font-bold text-gray-800 tracking-tight">Complete your estate</h2>
                <p className="text-gray-600 text-lg mt-2">Enter your own information below</p>
              </div>
              <div className="mt-8 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input label="Your first name" size="lg" type="text" value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} placeholder="Joe" />
                  <Input label="Your last name" size="lg" type="text" value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} placeholder="Bloggs" />
                </div>
                <Input label="Your email address" size="lg" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="joe.bloggs@gmail.com" />
                <Input label="Password" size="lg" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password" />
                <Input label="Confirm password" size="lg" type="password" value={newUser.password_confirmation} onChange={(e) => setNewUser({ ...newUser, password_confirmation: e.target.value })} placeholder="Confirm password" />
                <div className="space-y-4 pt-2">
                  <Checkbox
                    value={newUser.terms}
                    onChange={(e) => setNewUser({ ...newUser, terms: e.target.checked })}
                    label={(
                      <p>
                        I agree to the {' '}
                        <Link href="/legal/terms"><a target="_blank" className="underline">terms and conditions</a></Link> and  <Link href="/legal/privacy"><a target="_blank" className="underline">privacy policy</a></Link>
                      </p>
                    )}
                  />
                  <Checkbox label="Receive community insights" value={newUser.marketing} onChange={(e) => setNewUser({ ...newUser, marketing: e.target.checked })} />
                </div>
              </div>
              {
                errors?.length > 0 && (
                  <div className="mt-4">
                    <Alert type="error" list={errors} />
                  </div>
                )
              }
              <div className="flex flex-col space-y-4 mt-10">
                <Button color="black" type="submit" size="lg" disabled={submitting}>
                  {submitting ? 'Registering your estate...' : 'Register'}
                </Button>
                <Button color="clear" size="lg" onClick={() => setStep('SCENARIO')}>Back</Button>
              </div>
            </div>
          )
        }
      </form>
    </AuthLayout>
  )
}